import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function ArticleDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-24 pb-12 text-center">
        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
          <i className="ri-file-unknow-line text-3xl"></i>
        </div>
        <p className="text-foreground-500 text-sm mb-4">{t('article.noArticles')}</p>
        <Link to="/articles" className="text-primary-500 text-sm font-medium no-underline hover:text-primary-600">
          {t('article.backToList')}
        </Link>
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.summary,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.image,
        url: `https://huangtao.com/article/${article.id}`,
        publisher: {
          '@type': 'Organization',
          name: t('site.name'),
        },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://huangtao.com' },
          { '@type': 'ListItem', position: 2, name: t('nav.article'), item: 'https://huangtao.com/articles' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-24 pb-12 md:pb-16">
        <article className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), path: '/' },
              { label: t('nav.article'), path: '/articles' },
              { label: article.title },
            ]}
          />

          <div className="mb-6">
            <span className="text-xs text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full font-medium">
              {article.category}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-xs text-foreground-400 mb-6">
            <span><i className="ri-user-line mr-1"></i>{t('article.by')}：{article.author}</span>
            <span><i className="ri-calendar-line mr-1"></i>{t('article.publishedOn')} {article.date}</span>
            <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()} {t('article.views')}</span>
          </div>

          <div className="rounded-lg overflow-hidden mb-8 bg-background-200">
            <img
              src={article.image}
              alt={`${article.title} 封面图`}
              className="w-full aspect-[2/1] object-cover"
            />
          </div>

          <div
            className="prose prose-sm max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-2 mt-8 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-secondary-600 bg-secondary-100 px-2.5 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <div className="max-w-3xl mx-auto mt-12 pt-8 border-t border-background-200">
            <h3 className="text-lg font-bold text-foreground-900 mb-4">{t('article.relatedArticles')}</h3>
            <div className="flex flex-col gap-3">
              {relatedArticles.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.id}`}
                  className="group flex items-center gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors no-underline"
                >
                  <div className="w-16 h-12 flex-shrink-0 rounded overflow-hidden bg-background-200">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground-900 truncate block group-hover:text-primary-500 transition-colors">
                      {a.title}
                    </span>
                    <span className="text-xs text-foreground-400">{a.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}